const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// First-touch attribution window (days)
const ATTRIBUTION_WINDOW_DAYS = 90;
const SEARCH_ENGINES = [
  "google.", "bing.", "yahoo.", "duckduckgo.", "qwant.", "ecosia.",
  "brave.", "search.", "baidu.", "yandex."
];

const PAID_MEDIUMS = ["cpc", "ppc", "paid", "paidsearch", "paid_search", "paid-social", "cpm", "display"];

// Classify a visit into a canonical channel
// Priority: gclid / paid utm → google_ads, search engine → seo, external → other, else direct
function classifyChannel({ gclid, utmSource, utmMedium, referrer }) {
  if (gclid) return "google_ads";
  if (utmMedium && PAID_MEDIUMS.includes(String(utmMedium).toLowerCase())) {
    // utm_source=facebook + utm_medium=cpc → meta_ads (extensible)
    if (utmSource && utmSource.toLowerCase().includes("facebook")) return "meta_ads";
    return "google_ads";
  }
  if (referrer) {
    const refLower = referrer.toLowerCase();
    // Same-domain referrers do NOT count as external (handled by caller)
    if (SEARCH_ENGINES.some(eng => refLower.includes(eng))) return "seo";
    return "other";
  }
  return "direct";
}

function isOwnHost(url) {
  try {
    const host = new URL(url).hostname;
    return host === window.location.hostname || host.endsWith(".transfastdem.fr") || host.includes("emergentagent.com");
  } catch {
    return false;
  }
}

// Detect & persist FIRST-TOUCH source on landing
function detectAndPersistSource() {
  const params = new URLSearchParams(window.location.search);
  const gclid = params.get("gclid");
  const utmSource = params.get("utm_source");
  const utmMedium = params.get("utm_medium");
  const utmCampaign = params.get("utm_campaign");
  const utmTerm = params.get("utm_term");
  const utmContent = params.get("utm_content");
  // Phase 1 (2026-07-03) : capture every paid-click identifier so the
  // server-side attribution.classify() can produce granular channels.
  const fbclid = params.get("fbclid");
  const msclkid = params.get("msclkid");
  const gbraid = params.get("gbraid");
  const wbraid = params.get("wbraid");
  const ttclid = params.get("ttclid");
  const yclid = params.get("yclid");
  const li_fat_id = params.get("li_fat_id");
  const twclid = params.get("twclid");
  const sclkid = params.get("sclkid");

  // External referrer only (ignore same-site navigation)
  const rawRef = document.referrer || "";
  const externalRef = rawRef && !isOwnHost(rawRef) ? rawRef : "";

  // Read any previously stored source
  let stored = null;
  try {
    const raw = localStorage.getItem("tf_source");
    if (raw) {
      const parsed = JSON.parse(raw);
      const age = Date.now() - (parsed.first_seen_ts || 0);
      if (age < ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000) {
        stored = parsed;
      } else {
        // Expired → clear
        localStorage.removeItem("tf_source");
      }
    }
  } catch {}

  // If new attribution signal arrives, OVERWRITE the stored source
  // (last-non-direct wins: a fresh paid/SEO visit replaces an older direct entry,
  //  but a direct revisit does NOT erase a paid attribution)
  const anyPaidClickId = gclid || fbclid || msclkid || gbraid || wbraid || ttclid || yclid || li_fat_id || twclid || sclkid;
  const hasFreshSignal = !!(anyPaidClickId || utmSource || utmMedium || externalRef);
  const newChannel = hasFreshSignal
    ? classifyChannel({ gclid, utmSource, utmMedium, referrer: externalRef })
    : null;

  if (hasFreshSignal && newChannel) {
    // Only overwrite if:
    //  - no previous source, OR
    //  - previous was "direct" (any non-direct signal upgrades it), OR
    //  - we have a new paid click id (always refresh attribution window)
    const shouldOverwrite =
      !stored ||
      stored.channel === "direct" ||
      (anyPaidClickId && (newChannel === "google_ads" || newChannel === "meta_ads"));

    if (shouldOverwrite) {
      stored = {
        channel: newChannel,
        gclid: gclid || (stored && stored.gclid) || null,
        fbclid: fbclid || (stored && stored.fbclid) || null,
        msclkid: msclkid || (stored && stored.msclkid) || null,
        gbraid: gbraid || (stored && stored.gbraid) || null,
        wbraid: wbraid || (stored && stored.wbraid) || null,
        ttclid: ttclid || (stored && stored.ttclid) || null,
        yclid: yclid || (stored && stored.yclid) || null,
        li_fat_id: li_fat_id || (stored && stored.li_fat_id) || null,
        twclid: twclid || (stored && stored.twclid) || null,
        sclkid: sclkid || (stored && stored.sclkid) || null,
        utm_source: utmSource || (stored && stored.utm_source) || null,
        utm_medium: utmMedium || (stored && stored.utm_medium) || null,
        utm_campaign: utmCampaign || (stored && stored.utm_campaign) || null,
        utm_term: utmTerm || (stored && stored.utm_term) || null,
        utm_content: utmContent || (stored && stored.utm_content) || null,
        referrer: externalRef || (stored && stored.referrer) || null,
        landing_page: window.location.pathname + window.location.search,
        first_seen_ts: Date.now(),
        first_seen_at: new Date().toISOString(),
      };
      try { localStorage.setItem("tf_source", JSON.stringify(stored)); } catch {}
    }
  } else if (!stored) {
    // No previous source, no signal on this visit → record as direct (first touch)
    stored = {
      channel: "direct",
      gclid: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_term: null,
      utm_content: null,
      referrer: null,
      landing_page: window.location.pathname + window.location.search,
      first_seen_ts: Date.now(),
      first_seen_at: new Date().toISOString(),
    };
    try { localStorage.setItem("tf_source", JSON.stringify(stored)); } catch {}
  }

  return stored;
}

// Public API: get the persisted first-touch source (used by ChronoPage, contact form, etc.)
export function getStoredSource() {
  // Guarantee detection has run at least once for this page load.
  // This eliminates the historical race condition where the 1st page_view
  // (emitted by <PageViewTracker> in App.js) could fire BEFORE
  // initGlobalTracking() had persisted the gclid coming from the URL.
  ensureDetected();
  try {
    const raw = localStorage.getItem("tf_source");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const age = Date.now() - (parsed.first_seen_ts || 0);
    if (age >= ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem("tf_source");
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Idempotent : runs detection once per page load, as soon as the first consumer needs it.
function ensureDetected() {
  if (typeof window === "undefined") return;
  if (window.__tf_source_detected__) return;
  window.__tf_source_detected__ = true;
  try {
    detectAndPersistSource();
  } catch {
    // Swallow errors to avoid breaking page_view tracking in private/incognito mode
  }
}

// EAGER DETECTION AT MODULE LOAD
// Critical : we MUST persist the gclid/UTM from the current URL BEFORE any
// consumer (trackEvent, PageViewTracker, ChronoPage.getStoredSource) reads it.
// Historically the 1st page_view fired before detectAdSource() had run,
// causing ~99% of Ads clicks to be lost. This side-effect import guarantees
// the source is captured synchronously the moment analytics.js is first loaded.
if (typeof window !== "undefined") {
  ensureDetected();
}

export function trackEvent(eventType, metadata = {}) {
  const page = window.location.pathname;
  const source = getStoredSource();
  const body = { event_type: eventType, page, metadata };
  if (source) body.source = source;

  fetch(`${BACKEND_URL}/api/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch(() => {});
}

// Auto-instrument all tel:, wa.me links, and CTA buttons via event delegation
export function initGlobalTracking() {
  // First-touch attribution on every page load (idempotent)
  ensureDetected();

  document.addEventListener("click", (e) => {
    const link = e.target.closest("a[href]");
    if (link) {
      const href = link.getAttribute("href") || "";
      if (href.startsWith("tel:")) {
        trackEvent("call_click", { phone: href.replace("tel:", "") });
        return;
      }
      if (href.includes("wa.me")) {
        trackEvent("whatsapp_click", { url: href });
        return;
      }
    }
    const btn = e.target.closest("button[data-testid], a[data-testid]");
    if (btn) {
      const testId = btn.getAttribute("data-testid") || "";
      if (testId.includes("cta") || testId.includes("devis")) {
        trackEvent("cta_click", { label: testId, text: btn.textContent?.trim().substring(0, 50) });
      }
    }
  });

  document.addEventListener("submit", (e) => {
    const form = e.target;
    if (form && form.tagName === "FORM" && !window.location.pathname.startsWith("/admin")) {
      trackEvent("form_submit", { formId: form.id || form.getAttribute("data-testid") || "unknown" });
    }
  });
}
